import { INTEGRATION_STATUS_KEY } from './integrationStatusCache';
// utils/tokenStore.js
//
// Dual persistence for the admin session, built for the iOS home-screen PWA.
//
// WebKit flushes localStorage to disk ASYNCHRONOUSLY: force-killing the app
// (swipe-up) right after login can silently DROP the freshly written token →
// the user reopens the app logged out. Cookies are flushed synchronously and
// survive the kill, so every session write goes to BOTH stores and the cookie
// restores localStorage at boot when needed.
//
// The cookie mirrors the token + a SLIM user (essential fields only — the full
// user object can be big and cookies are capped at ~4KB).

const COOKIE_NAME = 'hostea_session';
const MAX_AGE_S = 30 * 24 * 60 * 60; // aligned with the JWT lifetime

const slimUser = (user) => {
  if (!user || typeof user !== 'object') return null;
  const { _id, id, email, firstName, lastName, companyId, type } = user;
  return { _id: _id || id, id: id || _id, email, firstName, lastName, companyId, type };
};

const writeCookie = (value) => {
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(value)}; max-age=${MAX_AGE_S}; path=/; SameSite=Lax${secure}`;
  } catch { /* never break auth flows over a cookie */ }
};

const readCookie = () => {
  try {
    const hit = document.cookie.split('; ').find((c) => c.startsWith(`${COOKIE_NAME}=`));
    return hit ? decodeURIComponent(hit.slice(COOKIE_NAME.length + 1)) : null;
  } catch {
    return null;
  }
};

/** Persist the session in BOTH stores (login, refresh, any token rotation). */
export const persistSession = (token, user = undefined) => {
  if (!token) return;
  localStorage.setItem('token', token);
  let u = user;
  if (u === undefined) {
    try { u = JSON.parse(localStorage.getItem('user') || 'null'); } catch { u = null; }
  } else if (u) {
    localStorage.setItem('user', JSON.stringify(u));
  }
  writeCookie(JSON.stringify({ t: token, u: slimUser(u) }));
};

/** At boot: if localStorage lost the session (iOS flush drop), restore from the cookie. */
export const restoreSession = () => {
  try {
    if (localStorage.getItem('token')) return; // primary store intact
    const raw = readCookie();
    if (!raw) return;
    const { t, u } = JSON.parse(raw);
    if (!t) return;
    localStorage.setItem('token', t);
    if (u && !localStorage.getItem('user')) localStorage.setItem('user', JSON.stringify(u));
    console.info('🔐 Session restored from cookie (localStorage was empty)');
  } catch { /* corrupt cookie → treated as logged out */ }
};

/** Real logout: clear BOTH stores. */
export const clearSession = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem(INTEGRATION_STATUS_KEY);
  try {
    document.cookie = `${COOKIE_NAME}=; max-age=0; path=/`;
  } catch { /* ignore */ }
};
